import api, { handleError } from '@/services/axiosInstance';
import type { AgentStreamRoute } from '@/types/agentStream';

import type { ConnieStreamEvent, ConnieToolCallOutput } from './connie';

const CONNIE_API_URL = 'https://api.vh3connect.io/api:f-Fs4_ed';

/**
 * A 60-second, single-use token for the FSI voice socket (Xano endpoint 2896).
 * Xano signs the user's company and user id into it, so the browser never sends
 * an FSI key or a tenant id.
 */
export type ConnieVoiceToken = {
  token: string;
  url: string;
  expiresAt?: number | string;
};

export const getConnieVoiceToken = async (): Promise<ConnieVoiceToken> => {
  try {
    const { data } = await api.post<ConnieVoiceToken>(
      `${CONNIE_API_URL}/connie/voice/token`,
      {}
    );
    if (!data?.token || !data?.url) {
      throw new Error('Voice is not available right now.');
    }
    return data;
  } catch (error) {
    return handleError(error, 'getConnieVoiceToken');
  }
};

export type ConnieVoiceMode = 'auto' | 'push_to_talk';

/** Who answers on the socket. FSI treats a config without `agent` as Connie. */
export type VoiceAgent = 'connie' | 'tia';

/** First message on the socket. See FSI `api/routers/voice_ws.py`. */
export type ConnieVoiceConfig = {
  type: 'config';
  token: string;
  mode: ConnieVoiceMode;
  silenceMs: number;
  /** Sent only for Tia, so Connie's config stays as it was. */
  agent?: VoiceAgent;
  /** The Library Document Tia answers about. Required with `agent: 'tia'`. */
  documentId?: string;
  sessionId?: string;
  userName?: string;
  userCompanyName?: string;
  contactId?: string;
  currentJobReference?: string;
  summary?: string;
};

/** The final answer of a voice turn: the stream's `done`, plus the transcript. */
export type ConnieVoiceAgentResponse = {
  sessionId: string;
  response?: string;
  toolsUsed?: string[];
  toolCallOutputs?: ConnieToolCallOutput[];
  transcript?: string;
  route?: AgentStreamRoute | null;
  usage?: unknown;
  turnIndex?: number;
  libraryReferences?: unknown;
  /** What filled the context window (FSI #1224); parsed with parseContextMap. */
  contextMap?: unknown;
  /** Tia only. */
  agent?: VoiceAgent;
  documentId?: string;
};

/**
 * Tia's document metadata, sent before the first tool. Same payload as the
 * Tia SSE stream's `document_context`.
 */
export type VoiceDocumentContextEvent = {
  type: 'document_context';
  data: {
    documentId?: string;
    name?: string;
    pageCount?: number;
    readingMode?: 'full_text' | 'page_guide';
    pageGuideSource?: 'stored' | 'fallback';
    agentVariant?: string;
  };
};

/** Events relayed from the agent runner with the same payloads as the SSE stream. */
export type RelayedStreamEvent =
  | Extract<
      ConnieStreamEvent,
      { type: 'progress' | 'tool_start' | 'tool_result' | 'text_delta' }
    >
  | VoiceDocumentContextEvent;

export type ConnieVoiceMessage =
  | { type: 'ready'; mode: ConnieVoiceMode }
  | { type: 'transcript'; text: string; final: boolean }
  | { type: 'end_of_turn'; transcript: string }
  | { type: 'processing'; transcript?: string }
  | { type: 'stream'; event: RelayedStreamEvent }
  | { type: 'agent_response'; data: ConnieVoiceAgentResponse }
  | { type: 'cancelled' }
  | { type: 'listening' }
  | { type: 'idle' }
  | { type: 'error'; message: string };

const RELAYED_EVENTS = new Set([
  'progress',
  'tool_start',
  'tool_result',
  'text_delta',
  'document_context',
]);

/** Parses one text frame from the voice socket; unknown types return null. */
export const parseConnieVoiceMessage = (
  raw: string
): ConnieVoiceMessage | null => {
  let payload: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    payload = parsed as Record<string, unknown>;
  } catch {
    return null;
  }

  const type = typeof payload.type === 'string' ? payload.type : '';
  const text = (value: unknown) => (typeof value === 'string' ? value : '');

  if (RELAYED_EVENTS.has(type)) {
    const { type: _type, ...data } = payload;
    if (type === 'progress' && !text(data.delta)) return null;
    return {
      type: 'stream',
      event: { type, data } as RelayedStreamEvent,
    };
  }

  switch (type) {
    case 'ready':
      return {
        type,
        mode: payload.mode === 'auto' ? 'auto' : 'push_to_talk',
      };
    case 'transcript':
      return { type, text: text(payload.text), final: payload.final === true };
    case 'end_of_turn':
      return { type, transcript: text(payload.transcript) };
    case 'processing':
      return { type, transcript: text(payload.transcript) || undefined };
    case 'agent_response': {
      const { type: _type, ...data } = payload;
      return { type, data: data as ConnieVoiceAgentResponse };
    }
    case 'cancelled':
    case 'listening':
    case 'idle':
      return { type };
    case 'error':
      return {
        type,
        message: text(payload.message) || 'Could not answer that.',
      };
    // `speaking` / `spoken` (spoken replies, FSI #1172) land here later.
    default:
      return null;
  }
};

/** Close codes the voice socket uses. */
export const VOICE_CLOSE = {
  NORMAL: 1000,
  CAPACITY: 1013,
  UNAUTHORIZED: 4401,
  TRANSCRIPTION_ENDED: 4020,
  /** Tia: the config has no `documentId`. */
  NO_DOCUMENT: 4003,
  /** Tia voice is switched off, or the agent is unknown. */
  AGENT_UNAVAILABLE: 4004,
} as const;
